import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Page,
  Card,
  IndexTable,
  Text,
  Badge,
  Tabs,
  InlineStack,
  BlockStack,
  Link,
  EmptyState,
  Spinner,
  Toast,
} from "@shopify/polaris";
import AppWrapper from "../AppWrapper";
import supabase from "../../supabase-client";
import { formatPrice } from "../../utility/funcs";
import { MIN_LISTED_PAYMENT_CENTS } from "../../utility/payments";
import ApplyPaymentModal from "./ApplyPaymentModal";
import CardBadge from "./CardBadge";

// Pagos recibidos: every payment that has arrived, and what is still unapplied.
//
// Rows come from `payments` (immutable facts) and `payment_balances` (derived).
// SPEI rows are written by pagos-svc the moment Fintoc confirms the transfer,
// already attributed to the customer/company that owns the CLABE — so this page
// never asks "whose is this?", only "what does it cover?". The exception is a
// payment to the root CLABE, shown as "Sin identificar".
//
// Live: a new SPEI appears without reloading.

const TABS = [
  { id: "pending", content: "Por aplicar" },
  { id: "all", content: "Todos" },
];

const RAIL_LABEL = { spei: "SPEI", card: "Tarjeta", cash: "Efectivo" };
const money = (c) => formatPrice(c / 100);

// Who sent it, in the terms of its own rail: a SPEI has a sender name, a card
// has a network mark and four digits and often no name at all.
const Sender = ({ payment: p }) => {
  if (p.rail === "card" && (p.card_brand || p.card_last4)) {
    return (
      <InlineStack gap="150" blockAlign="center" wrap={false}>
        {p.counterparty_name && (
          <Text as="span" tone="subdued">{p.counterparty_name}</Text>
        )}
        <CardBadge brand={p.card_brand} last4={p.card_last4} funding={p.card_type} />
      </InlineStack>
    );
  }
  return <Text as="span" tone="subdued">{p.counterparty_name || "—"}</Text>;
};

function PagosRecibidos() {
  const [tab, setTab] = useState(0);
  const [rows, setRows] = useState(null);
  const [names, setNames] = useState({});
  const [selected, setSelected] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    const [{ data: pays }, { data: bals }, { data: refundEgresos }] = await Promise.all([
      supabase
        .from("payments")
        .select("*")
        .eq("direction", "in")
        .eq("mode", "live")
        .gte("amount_cents", MIN_LISTED_PAYMENT_CENTS)
        // A tercero's return is applied on its egreso, not to a reservation.
        .or("payer_type.is.null,payer_type.neq.payee")
        .order("occurred_at", { ascending: false })
        .limit(300),
      supabase.from("payment_balances").select("payment_id, applied_cents, unapplied_cents"),
      // "Devolver": money an open refund egreso still owes is held, not unapplied.
      supabase
        .from("egresos")
        .select("id, refund_payment_id")
        .not("refund_payment_id", "is", null)
        .is("closed_at", null)
        .neq("approval", "rechazada"),
    ]);
    const bal = Object.fromEntries((bals || []).map((b) => [b.payment_id, b]));
    const held = {};
    if (refundEgresos?.length) {
      const { data: rb } = await supabase
        .from("egreso_balances")
        .select("egreso_id, por_pagar_cents")
        .in("egreso_id", refundEgresos.map((g) => g.id));
      const owed = Object.fromEntries((rb || []).map((b) => [b.egreso_id, Number(b.por_pagar_cents)]));
      for (const g of refundEgresos) {
        if (owed[g.id] > 0) held[g.refund_payment_id] = (held[g.refund_payment_id] || 0) + owed[g.id];
      }
    }
    const list = (pays || []).map((p) => ({
      ...p,
      applied_cents: Number(bal[p.id]?.applied_cents || 0),
      // Raw: the modal subtracts the held refund itself, from fresher numbers.
      raw_unapplied_cents: Number(bal[p.id]?.unapplied_cents ?? p.amount_cents),
      held_cents: held[p.id] || 0,
      unapplied_cents: Math.max(0, Number(bal[p.id]?.unapplied_cents ?? p.amount_cents) - (held[p.id] || 0)),
    }));
    setRows(list);

    const custIds = [...new Set(list.filter((p) => p.payer_type === "customer").map((p) => p.payer_id))];
    const compIds = [...new Set(list.filter((p) => p.payer_type === "company").map((p) => p.payer_id))];
    const [{ data: cs }, { data: co }] = await Promise.all([
      custIds.length
        ? supabase.from("customers").select("id, first_name, last_name").in("id", custIds)
        : { data: [] },
      compIds.length ? supabase.from("companies").select("id, name").in("id", compIds) : { data: [] },
    ]);
    const n = {};
    for (const c of cs || []) n[`customer:${c.id}`] = `${c.first_name || ""} ${c.last_name || ""}`.replace(/\s+/g, " ").trim();
    for (const c of co || []) n[`company:${c.id}`] = c.name;
    setNames(n);
    return list;
  }, []);

  useEffect(() => {
    load();
    const channel = supabase
      .channel("pagos-recibidos")
      .on("postgres_changes", { event: "*", schema: "public", table: "payments" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "payment_applications" }, () => load())
      .on("postgres_changes", { event: "*", schema: "public", table: "egresos" }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [load]);

  // Keep the open modal pointed at fresh numbers after an apply/reverse.
  useEffect(() => {
    if (!selected || !rows) return;
    const fresh = rows.find((r) => r.id === selected.id);
    if (fresh && fresh.raw_unapplied_cents !== selected.raw_unapplied_cents) setSelected(fresh);
  }, [rows, selected]);

  const visible = useMemo(
    () => (rows || []).filter((r) => TABS[tab].id === "all" || r.unapplied_cents >= MIN_LISTED_PAYMENT_CENTS),
    [rows, tab]
  );
  const pendingTotal = (rows || []).reduce((s, r) => s + Math.max(0, r.unapplied_cents), 0);
  const pendingCount = (rows || []).filter((r) => r.unapplied_cents >= MIN_LISTED_PAYMENT_CENTS).length;

  const payerName = (p) => (p.payer_type ? names[`${p.payer_type}:${p.payer_id}`] || `#${p.payer_id}` : null);
  const payerUrl = (p) => (p.payer_type === "company" ? `/companies/edit?id=${p.payer_id}` : `/customers/edit?id=${p.payer_id}`);

  const status = (p) => {
    if (!p.payer_type) return <Badge tone="critical">Sin identificar</Badge>;
    if (p.unapplied_cents <= 0 && p.held_cents > 0) return <Badge tone="attention">En devolución</Badge>;
    if (p.unapplied_cents <= 0) return <Badge tone="success">Aplicado</Badge>;
    if (p.applied_cents > 0) return <Badge tone="attention">Parcial</Badge>;
    return <Badge tone="warning">Por aplicar</Badge>;
  };

  return (
    <AppWrapper>
      <Page
        title="Pagos recibidos"
        subtitle={
          rows && pendingCount
            ? `${pendingCount} por aplicar · ${money(pendingTotal)}`
            : "Todo aplicado"
        }
      >
        <Card padding="0">
          <Tabs tabs={TABS} selected={tab} onSelect={setTab} />
          {rows === null ? (
            <div style={{ padding: 24, textAlign: "center" }}><Spinner size="small" /></div>
          ) : visible.length === 0 ? (
            <EmptyState heading="Nada por aplicar" image="">
              <p>Los SPEI a las CLABEs de clientes y los cobros con tarjeta aparecen aquí en cuanto llegan.</p>
            </EmptyState>
          ) : (
            <IndexTable
              resourceName={{ singular: "pago", plural: "pagos" }}
              itemCount={visible.length}
              selectable={false}
              headings={[
                { title: "Fecha" },
                { title: "Cliente" },
                { title: "Concepto" },
                { title: "Envió" },
                { title: "Monto", alignment: "end" },
                { title: "Por aplicar", alignment: "end" },
                { title: "Estado" },
              ]}
            >
              {visible.map((p, i) => (
                <IndexTable.Row key={p.id} id={String(p.id)} position={i} onClick={() => setSelected(p)}>
                  <IndexTable.Cell>
                    <BlockStack gap="050">
                      <Text as="span">
                        {new Date(p.occurred_at).toLocaleDateString("es-MX", { day: "numeric", month: "short" })}
                      </Text>
                      <Text as="span" tone="subdued" variant="bodySm">
                        {new Date(p.occurred_at).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" })}
                        {" · "}
                        {RAIL_LABEL[p.rail] || p.rail}
                      </Text>
                    </BlockStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    {p.payer_type ? (
                      // stopPropagation: the link opens the profile, the row opens the modal
                      <span onClick={(e) => e.stopPropagation()}>
                        <Link url={payerUrl(p)} removeUnderline>{payerName(p)}</Link>
                      </span>
                    ) : (
                      <Text as="span" tone="subdued">—</Text>
                    )}
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Text as="span">{p.concept || "—"}</Text>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <Sender payment={p} />
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack align="end"><Text as="span" numeric>{money(p.amount_cents)}</Text></InlineStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>
                    <InlineStack align="end">
                      <Text as="span" numeric fontWeight={p.unapplied_cents > 0 ? "semibold" : undefined}>
                        {p.unapplied_cents > 0 ? money(p.unapplied_cents) : "—"}
                      </Text>
                    </InlineStack>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{status(p)}</IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          )}
        </Card>
      </Page>

      {toast && (
        <Toast
          content={toast.content}
          error={toast.error}
          duration={toast.error ? 12000 : 5000}
          onDismiss={() => setToast(null)}
        />
      )}

      {selected && (
        <ApplyPaymentModal
          open={!!selected}
          payment={selected}
          payerName={payerName(selected)}
          unappliedCents={selected.raw_unapplied_cents}
          onClose={() => setSelected(null)}
          onApplied={(opts) => {
            if (opts?.message) setToast({ content: opts.message, error: opts.tone === "warning" });
            if (!opts?.keepOpen) setSelected(null);
          }}
        />
      )}
    </AppWrapper>
  );
}

export default PagosRecibidos;
